import { Grid } from '@atgfr8/atg-ui-tailwind';
import { BasicModal } from '@components';
import {
  fetchLtlApiRequestSummary,
  fetchLtlApiRequestDetail,
} from '@store/orders/services';
import {
  LtlApiRequestSummaryViewModel,
  LtlApiRequestDetailsViewModel,
} from '@store/orders/types';
import { copyToClipboard } from '@utils/copyToClipBoard';
import { For, Show, createEffect, createSignal } from 'solid-js';

export const ApiRequestModal = (props: {
  onClose: (modalId: string) => void;
  modalId: string;
  quoteId: string;
}) => {
  const [summaryList, setSummaryList] = createSignal<
    LtlApiRequestSummaryViewModel[]
  >([]);
  const [expandedId, setExpandedId] = createSignal<number | null>(null);
  const [details, setDetails] =
    createSignal<LtlApiRequestDetailsViewModel | null>(null);
  const [copiedField, setCopiedField] = createSignal<
    'request' | 'response' | null
  >(null);

  createEffect(async () => {
    const summary = await fetchLtlApiRequestSummary(props.quoteId);
    setSummaryList(summary);
  });

  const toggleExpanded = async (id: number) => {
    if (expandedId() === id) {
      setExpandedId(null);
      setDetails(null);
    } else {
      setExpandedId(id);
      const detail = await fetchLtlApiRequestDetail(id);
      setDetails(detail);
    }
  };

  function prettifyXml(xml: string): string {
    const PADDING = '  ';
    let formatted = '';
    let pad = 0;
    xml
      .replace(/>\s*</g, '><')
      .replace(/</g, '~::~<')
      .split('~::~')
      .forEach((node) => {
        if (!node) return;

        if (node.match(/^<\?.*\?>$/)) {
          formatted += `${PADDING.repeat(pad)}${node}\n`;
        } else if (node.match(/^<\/\w/)) {
          pad = Math.max(pad - 1, 0);
          formatted += `${PADDING.repeat(pad)}${node}\n`;
        } else if (node.match(/^<\w([^>]*[^/])?>.*$/)) {
          formatted += `${PADDING.repeat(pad)}${node}\n`;
          pad++;
        } else {
          formatted += `${PADDING.repeat(pad)}${node}\n`;
        }
      });

    return formatted.trim();
  }

  const renderFormattedBlock = (
    content: string | null | undefined,
    type: 'request' | 'response',
  ) => {
    let formatted = content ?? '';
    const trimmed = formatted.trim();

    const isJson = trimmed.startsWith('{') || trimmed.startsWith('[');
    const isXml = trimmed.startsWith('<');

    if (isJson) {
      try {
        formatted = JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch {
        formatted = trimmed;
      }
    } else if (isXml) {
      formatted = prettifyXml(trimmed);
    }

    return (
      <div class="mt-4 first:mt-0">
        <div class="flex items-center justify-between mb-1">
          <span class="font-semibold capitalize">{type}:</span>
          <button
            class="text-blue-600 text-xs hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              void copyToClipboard(formatted);
              setCopiedField(type);
            }}
          >
            {copiedField() === type ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <pre class="bg-white p-3 overflow-auto max-h-60 border rounded-sm text-xs font-mono whitespace-pre-wrap leading-snug text-gray-800">
          {formatted}
        </pre>
      </div>
    );
  };

  return (
    <BasicModal
      id={props.modalId}
      title="API Call History"
      width="80vw"
      onClose={() => props.onClose(props.modalId)}
      footer={false}
      showClose
    >
      <div class="overflow-auto max-h-[70vh]">
        <Show
          when={summaryList().length > 0}
          fallback={
            <div class="p-4 text-gray-600 text-sm">
              API Request Summary does not exist for this quote.
            </div>
          }
        >
          <Grid
            container
            class="font-semibold border-b py-2 px-4 bg-gray-100 text-sm"
          >
            <Grid item size={4}>
              Endpoint
            </Grid>
            <Grid item size={2}>
              Type
            </Grid>
            <Grid item size={2}>
              Time (ms)
            </Grid>
            <Grid item size={2}>
              Created By
            </Grid>
            <Grid item size={2}>
              Created Date
            </Grid>
          </Grid>

          <For each={summaryList()}>
            {(req) => (
              <>
                <Grid
                  container
                  class="border-b py-2 px-4 text-sm cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleExpanded(req.id)}
                >
                  <Grid item size={4} class="truncate">
                    {req.endpoint}
                  </Grid>
                  <Grid item size={2}>
                    {req.apiRequestTypeDescription}
                  </Grid>
                  <Grid item size={2}>
                    {req.responseTimeMs}
                  </Grid>
                  <Grid item size={2}>
                    {req.createdBy}
                  </Grid>
                  <Grid item size={2}>
                    {new Date(req.createdDate).toLocaleString()}
                  </Grid>
                </Grid>

                <Show when={expandedId() === req.id && details()}>
                  <div class="bg-gray-50 p-4 border-b text-sm">
                    {renderFormattedBlock(details()?.request, 'request')}
                    {renderFormattedBlock(details()?.response, 'response')}
                  </div>
                </Show>
              </>
            )}
          </For>
        </Show>
      </div>
    </BasicModal>
  );
};
